import { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Highlight } from "@tiptap/extension-highlight";
import { marked } from "marked";
import DOMPurify from "dompurify";

export interface AnchoredQuote {
  quote: string;
  contextBefore?: string;
  contextAfter?: string;
}

export interface HighlightedComment {
  id: string;
  quote: string;
  authorLabel: string;
  decision: "approve" | "return" | "note" | null;
}

interface ArticleRenderProps {
  content: string;
  onQuoteSelected?: (q: AnchoredQuote) => void;
  highlights?: HighlightedComment[];
}

/**
 * Read-only article view with selection-to-comment. The reviewer reads the
 * draft, highlights any span, and a floating "Comment" pill appears — clicking
 * it bubbles the quote (with up to 80 chars of context on each side) back up
 * so the page can pre-fill the comment box and remember the anchor.
 */
export function ArticleRender({ content, onQuoteSelected, highlights }: ArticleRenderProps) {
  const html = useMemo(() => {
    const parsed = marked.parse(content, { async: false }) as string;
    return DOMPurify.sanitize(parsed);
  }, [content]);

  const editor = useEditor(
    {
      extensions: [StarterKit, Highlight.configure({ multicolor: false })],
      content: html,
      editable: false,
      immediatelyRender: false,
    },
    [html],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [bubble, setBubble] = useState<{ x: number; y: number; quote: AnchoredQuote } | null>(null);

  useEffect(() => {
    function onSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) {
        setBubble(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = containerRef.current;
      if (!container || !container.contains(range.commonAncestorContainer)) {
        setBubble(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 2) {
        setBubble(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      const { contextBefore, contextAfter } = extractContext(container, range, 80);
      setBubble({
        x: rect.right - cRect.left,
        y: rect.top - cRect.top - 36,
        quote: { quote: text, contextBefore, contextAfter },
      });
    }
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  useEffect(() => {
    if (!editor) return;
    // Clear previous highlight marks then add fresh ones based on quotes.
    editor.chain().setTextSelection({ from: 0, to: editor.state.doc.content.size }).unsetMark("highlight").setTextSelection(0).run();
    if (!highlights || highlights.length === 0) return;
    const docText = editor.state.doc.textBetween(0, editor.state.doc.content.size, "\n", " ");
    for (const h of highlights) {
      const idx = docText.indexOf(h.quote);
      if (idx < 0) continue;
      const from = posFromTextIndex(editor, idx);
      const to = posFromTextIndex(editor, idx + h.quote.length);
      if (from != null && to != null && from < to) {
        editor.chain().setTextSelection({ from, to }).setMark("highlight").run();
      }
    }
    editor.chain().setTextSelection(0).run();
  }, [editor, highlights]);

  if (!editor) return null;

  return (
    <div className="article-render" ref={containerRef}>
      <EditorContent editor={editor} />
      {bubble && onQuoteSelected && (
        <button
          type="button"
          className="article-bubble"
          style={{ left: bubble.x, top: bubble.y }}
          onMouseDown={(e) => {
            // Prevent selection from clearing before click fires.
            e.preventDefault();
          }}
          onClick={() => {
            onQuoteSelected(bubble.quote);
            window.getSelection()?.removeAllRanges();
            setBubble(null);
          }}
        >
          💬 Comment on selection
        </button>
      )}
    </div>
  );
}

function extractContext(
  container: HTMLElement,
  range: Range,
  span: number,
): { contextBefore: string; contextAfter: string } {
  const full = container.innerText ?? container.textContent ?? "";
  const selected = range.toString();
  const idx = full.indexOf(selected);
  if (idx < 0) return { contextBefore: "", contextAfter: "" };
  const before = full.slice(Math.max(0, idx - span), idx);
  const after = full.slice(idx + selected.length, idx + selected.length + span);
  return { contextBefore: before, contextAfter: after };
}

function posFromTextIndex(editor: Editor, target: number): number | null {
  let acc = 0;
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found != null) return false;
    if (node.isText) {
      const text = node.text ?? "";
      if (acc + text.length >= target) {
        found = pos + (target - acc);
        return false;
      }
      acc += text.length;
    } else if (node.isBlock && acc > 0) {
      // Approximate one newline between blocks; cheap heuristic for our matcher.
      acc += 1;
    }
    return true;
  });
  return found;
}

/**
 * Heuristic — does this output look like an article worth rendering with the
 * rich-text view? Plain strings under ~200 chars stay as paragraphs; longer
 * strings, or strings with markdown markers, get the full treatment.
 */
export function looksLikeArticle(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value.length < 200) return false;
  if (/#{1,6}\s/.test(value)) return true;              // headings (anywhere)
  if (/\n\s*\n/.test(value)) return true;                // paragraph breaks
  if (/^[-*]\s/m.test(value)) return true;               // lists
  if (/\*\*[^*]+\*\*/.test(value)) return true;          // bold runs
  return false;
}
